import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Switch,
  FormHelperText,
  FormLabel,
  Grid
} from '@mui/material';
import { Save, ArrowLeft } from 'lucide-react';
import { ProfileService } from '../../services/profileService';
import { PermissionService, Permission } from '../../services/permissionService';
import { Profile, ProfilePermissions } from '../../types/profile';
import { useForm, Controller, SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';

interface ProfileFormData extends Partial<Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>> {
  permissions?: ProfilePermissions;
}

const ProfileFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const profileService = useMemo(() => new ProfileService(), []);
  const permissionService = useMemo(() => new PermissionService(), []);
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const { 
    control, 
    handleSubmit, 
    reset, 
    setValue,
    formState: { errors, isSubmitting } 
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: '',
      ativo: true,
      permissions: {}
    }
  });

  useEffect(() => {
    let isMounted = true;
    setLoadingPermissions(true);
    permissionService.getAll()
      .then((data: Permission[]) => {
        if (isMounted) {
          setPermissions(data);
          if (!isEditMode) {
            const initialPermissions = data.reduce((acc: { [key: string]: boolean }, p: Permission) => {
              acc[p.value] = false;
              return acc;
            }, {});
            setValue('permissions', initialPermissions);
          }
        }
      })
      .catch((err: any) => {
        if (isMounted) {
          console.error("Erro ao buscar permissões:", err);
          setError('Falha ao carregar lista de permissões.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoadingPermissions(false);
        }
      });

    if (isEditMode && id) {
      setLoading(true);
      profileService.getById(parseInt(id, 10))
        .then((profileData: Profile) => {
          if (isMounted) {
            const profilePermissions = permissions.reduce((acc: { [key: string]: boolean }, p: Permission) => {
              acc[p.value] = profileData.permissions[p.value] ?? false;
              return acc;
            }, {});
            
            reset({
              name: profileData.name,
              ativo: profileData.ativo,
              permissions: permissions.length > 0 ? profilePermissions : {}
            });
            setError(null);
          }
        })
        .catch((err: any) => {
          if (isMounted) {
            console.error("Erro ao buscar perfil para edição:", err);
            setError(err.message || 'Falha ao carregar dados do perfil.');
          }
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false);
          }
        });
    } else {
      reset({
        name: '',
        ativo: true,
        permissions: {}
      });
    }

    return () => {
      isMounted = false;
    };
  }, [id, isEditMode, profileService, permissionService, setValue, reset]);

  const handleConfirmSave: SubmitHandler<ProfileFormData> = async (data) => {
    setIsConfirmDialogOpen(false);
    setError(null);

    const dataToSubmit: Partial<Profile> & { permissions: ProfilePermissions } = {
      name: data.name,
      ativo: data.ativo,
      permissions: data.permissions || {},
    };

    try {
      let successMessage = '';
      if (isEditMode && id) {
        await profileService.update(parseInt(id, 10), dataToSubmit as any);
        successMessage = 'Perfil atualizado com sucesso.';
      } else {
        await profileService.create(dataToSubmit as any);
        successMessage = 'Perfil criado com sucesso.';
      }

      setSnackbarMessage(successMessage);
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/profiles');
      }, 1500);

    } catch (err: any) {
      console.error("Erro ao salvar perfil:", err);
      setError(err.message || 'Erro ao salvar perfil.');
    }
  };

  const onSubmit: SubmitHandler<ProfileFormData> = (data) => {
    console.log('Validação OK. Abrindo diálogo...');
    setIsConfirmDialogOpen(true);
  };

  const onInvalid: SubmitErrorHandler<ProfileFormData> = (errors) => {
    console.error('Erro de validação:', errors);
    setError("Erro de validação. Verifique os campos marcados.");
  };

  const handleCancelSave = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    navigate('/profiles');
  };

  if (loadingPermissions || (isEditMode && loading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEditMode ? 'Editar Perfil de Acesso' : 'Novo Perfil de Acesso'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}

      <Card elevation={2}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Nome do perfil é obrigatório' }}
                  render={({ field: { onChange, value, ...restField } }) => (
                    <TextField
                      {...restField}
                      value={value || ''}
                      onChange={(e) => {
                        const upperCaseValue = e.target.value.toUpperCase();
                        onChange(upperCaseValue);
                      }}
                      label="Nome do Perfil"
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      disabled={isSubmitting}
                      size="small"
                      sx={{ 
                        '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' },
                        '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                        '& .MuiFormHelperText-root': { fontSize: '0.75rem', marginTop: '2px' }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', pt: 2.5 }}>
                <Controller
                  name="ativo"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch 
                          {...field} 
                          checked={field.value ?? false}
                          disabled={isSubmitting}
                          size="small"
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.85rem' }}>Ativo</Typography>}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sx={{mt: 1}}>
                <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.9rem', fontWeight: 'bold' }}>Permissões</FormLabel>
                <FormGroup>
                  <Grid container spacing={0}>
                    {permissions.map((permission) => (
                      <Grid item xs={12} sm={6} md={4} key={permission.id}>
                        <Controller
                          name={`permissions.${permission.value}`}
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  {...field} 
                                  checked={field.value ?? false}
                                  disabled={isSubmitting}
                                  size="small"
                                />
                              }
                              label={<Typography sx={{ fontSize: '0.85rem' }}>{permission.name}</Typography>} 
                              title={permission.name}
                            />
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<ArrowLeft size={16} />}
                disabled={isSubmitting}
                size="small"
                sx={{ 
                  px: 2, 
                  py: 0.5,
                  height: 36,
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                CANCELAR
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                disabled={isSubmitting}
                size="small"
                sx={{ 
                  px: 2, 
                  py: 0.5,
                  height: 36,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                {isSubmitting ? 'SALVANDO...' : 'SALVAR'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={handleCancelSave}
        onConfirm={handleSubmit(handleConfirmSave)}
        title={isEditMode ? "Confirmar Alteração" : "Confirmar Criação"}
        contentText={`Tem certeza que deseja salvar ${isEditMode ? 'as alterações neste' : 'este novo'} perfil?`}
        isLoading={isSubmitting}
        confirmText={isSubmitting ? 'Salvando...' : 'Confirmar'}
      />

      <SuccessSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default ProfileFormPage; 